import { baseHeight, baseWidth } from '@/lib/OrgNodeData.js'
import { createGlobalState, useStorage } from '@vueuse/core'
import { computed, readonly, ref, unref } from 'vue'

const useNodeSizes = createGlobalState((boardId) => {
  const lastHeight = useStorage(`last-height-${unref(boardId)}`, 0)

  // Use shallow refs to avoid deep proxies for large maps
  const nodeWidths = ref({})
  const nodeHeights = ref({})

  const maxWidth = computed(() => {
    let max = 0

    Object.keys(nodeWidths.value).forEach((node) => {
      if (nodeWidths.value[node] > max) max = nodeWidths.value[node]
    })

    return max || baseWidth
  })

  const maxHeight = computed(() => {
    let max = 0
    const heights = Object.values(nodeHeights.value)

    // If there are no nodes, return base height
    if (heights.length === 0) return baseHeight

    // Return maximum height from all nodes
    heights.forEach((height) => {
      if (height > max) max = height
    })

    if (max) {
      lastHeight.value = max
    }

    return max || lastHeight.value || baseHeight
  })

  // minHeight for virtual scrollers - uses actual minimum from nodeHeights
  // This helps DynamicScroller estimate item heights more accurately on initial render
  const minHeight = computed(() => {
    const heights = Object.values(nodeHeights.value)

    if (heights.length === 0) return lastHeight.value || baseHeight

    // Find minimum height from rendered nodes
    // Using a loop instead of Math.min(...heights) for better performance with large arrays (10k+ nodes)
    let min = Infinity
    for (const height of heights) {
      if (height < min) min = height
    }
    return (min === Infinity ? lastHeight.value : min) || baseHeight
  })

  return {
    maxWidth,
    maxHeight,
    minHeight,
    nodeWidths,
    nodeHeights,
    lastHeight
  }
})

const useHoveredNode = createGlobalState(() => {
  const hoveredNode = ref(null)
  const hoveredSubordinates = ref([])
  const hoveredSubordinatesSet = computed(() => new Set(hoveredSubordinates.value || []))

  return {
    hoveredNode,
    hoveredSubordinates,
    hoveredSubordinatesSet
  }
})

const useHighlightedNode = createGlobalState(() => {
  const highlightedNode = ref(null)
  // 'ai' when the AI chat targeted the node, so the card can glow instead of ring.
  const highlightVariant = ref(null)
  // Nodes the AI chat is still about (pending proposals, the latest reply's
  // subject). A condition, not an event: cards glow steadily while listed.
  const aiFocusedNodes = ref([])

  return {
    highlightedNode,
    highlightVariant,
    aiFocusedNodes
  }
})

const useCopiedId = createGlobalState(() => {
  const copiedId = ref(null)

  return {
    copiedId
  }
})

const useHideImages = createGlobalState(() => {
  const hideImages = ref(false)

  return {
    hideImages
  }
})

const useNodeDropdown = createGlobalState(() => {
  const nodeDropdown = ref(null)

  return {
    nodeDropdown
  }
})

/**
 * Shared state of visible nodes to help set a common width/height when content size changes
 */
export default function useOrgNodeHelpers(boardId) {
  const { nodeWidths, nodeHeights, maxHeight, maxWidth, minHeight } = useNodeSizes(boardId)
  const { highlightedNode, highlightVariant, aiFocusedNodes } = useHighlightedNode()
  const { hoveredNode, hoveredSubordinates, hoveredSubordinatesSet } = useHoveredNode()
  const { hideImages } = useHideImages()
  const { copiedId } = useCopiedId()
  const { nodeDropdown } = useNodeDropdown()

  /**
   * Update width of a card with given id
   * @param id
   * @param width
   */
  // Batch width updates to a single commit per frame
  const pendingWidths = {}
  let rafWidths = 0
  const flushWidths = () => {
    const next = { ...nodeWidths.value }
    for (const key in pendingWidths) {
      const w = pendingWidths[key]
      if (w == null) delete next[key]
      else next[key] = w
      delete pendingWidths[key]
    }
    nodeWidths.value = next
    rafWidths = 0
  }

  const updateWidth = ({ id, width }) => {
    if (!id) return
    // allow null to delete
    pendingWidths[id] = width ?? null
    if (!rafWidths) rafWidths = requestAnimationFrame(flushWidths)
  }

  /**
   * Update height of a card with given id
   * @param id
   * @param width
   */
  // Batch height updates to reduce repeated maxHeight recompute and layout work
  const pendingHeights = {}
  let rafHeights = 0
  const flushHeights = () => {
    const next = { ...nodeHeights.value }
    for (const key in pendingHeights) {
      const h = pendingHeights[key]
      if (h == null) delete next[key]
      else next[key] = h
      delete pendingHeights[key]
    }
    nodeHeights.value = next
    rafHeights = 0
  }

  const updateHeight = ({ id, height }) => {
    if (!id) return
    pendingHeights[id] = height ?? null
    if (!rafHeights) rafHeights = requestAnimationFrame(flushHeights)
  }

  const reset = () => {
    nodeWidths.value = {}
    nodeHeights.value = {}
    copiedId.value = null
    hoveredNode.value = null
  }

  const setHovered = (id) => {
    hoveredNode.value = id
  }

  const setHoveredSubordinates = (ids) => {
    hoveredSubordinates.value = ids
  }

  const resetHovered = (id) => {
    if (id !== hoveredNode.value) return

    hoveredNode.value = null
  }

  const setCopiedId = (id) => {
    copiedId.value = id
  }

  const setHideImages = (value) => {
    hideImages.value = value
  }

  const triggerHighlight = (id, variant = null) => {
    highlightVariant.value = variant
    highlightedNode.value = id
  }

  const resetHighlight = () => {
    highlightedNode.value = null
    highlightVariant.value = null
  }

  const setAiFocus = (ids) => {
    aiFocusedNodes.value = ids
  }

  const setNodeDropdown = (id) => {
    nodeDropdown.value = id
  }

  const resetNodeDropdown = () => {
    nodeDropdown.value = null
  }

  return {
    copiedId: readonly(copiedId),
    hideImages: readonly(hideImages),
    highlightedNode: readonly(highlightedNode),
    highlightVariant: readonly(highlightVariant),
    aiFocusedNodes: readonly(aiFocusedNodes),
    hoveredNode: readonly(hoveredNode),
    hoveredSubordinates: readonly(hoveredSubordinates),
    hoveredSubordinatesSet: readonly(hoveredSubordinatesSet),
    maxHeight,
    maxWidth,
    minHeight,
    nodeHeights: readonly(nodeHeights),
    reset,
    resetHovered,
    setCopiedId,
    setHideImages,
    setHovered,
    setHoveredSubordinates,
    triggerHighlight,
    updateHeight,
    updateWidth,
    resetHighlight,
    setAiFocus,
    setNodeDropdown,
    resetNodeDropdown
  }
}
